/**
 * OfflineFailuresDialog — a simple, always-visible list of items, purchases
 * and ADR reports that failed to sync (not just "still pending"), so
 * nothing silently retries forever in the background. Reachable from the
 * header connection chip and from the Inventory page's own sync badge.
 */
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Loader2, Package, RefreshCw, Trash2, Truck } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  getAllOfflineProducts, getAllOfflinePurchases, getAllOfflineAdrReports,
  discardOfflineProduct, discardOfflinePurchase, discardOfflineAdrReport,
} from '../lib/offlineDB';
import { syncPendingProducts, syncPendingPurchases, syncPendingAdrReports } from '../lib/syncService';

const TYPES = [
  { key: 'product', icon: Package, title: 'New items', get: getAllOfflineProducts, retry: syncPendingProducts, discard: discardOfflineProduct, label: (r) => r.name || 'Untitled item' },
  { key: 'purchase', icon: Truck, title: 'New purchases', get: getAllOfflinePurchases, retry: syncPendingPurchases, discard: discardOfflinePurchase, label: (r) => `${r.supplier_name || 'Receipt'}${r.invoice_number ? ` · ${r.invoice_number}` : ''} (${(r.items || []).length} item${(r.items || []).length === 1 ? '' : 's'})` },
  { key: 'adr', icon: AlertTriangle, title: 'Reaction reports', get: getAllOfflineAdrReports, retry: syncPendingAdrReports, discard: discardOfflineAdrReport, label: (r) => r.medicine_name || 'Reaction report' },
];

export default function OfflineFailuresDialog({ open, onOpenChange, onChanged }) {
  const [rows, setRows] = useState({ product: [], purchase: [], adr: [] });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null); // `${type}:${local_id}` or `retry:${type}`

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const results = await Promise.all(TYPES.map((t) => t.get()));
      setRows({
        product: results[0].filter((r) => r.status === 'failed'),
        purchase: results[1].filter((r) => r.status === 'failed'),
        adr: results[2].filter((r) => r.status === 'failed'),
      });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const total = rows.product.length + rows.purchase.length + rows.adr.length;

  const retryType = async (type) => {
    setBusy(`retry:${type}`);
    try {
      const def = TYPES.find((t) => t.key === type);
      const result = await def.retry();
      if (result.error) toast.error('Still offline — will retry automatically');
      else toast.success(result.synced > 0 ? `Synced ${result.synced}` : 'Still failing — see the error below');
      await load();
      onChanged?.();
    } finally {
      setBusy(null);
    }
  };

  const discardRow = async (type, row) => {
    setBusy(`${type}:${row.local_id}`);
    try {
      const def = TYPES.find((t) => t.key === type);
      await def.discard(row.local_id);
      toast.success('Discarded');
      await load();
      onChanged?.();
    } finally {
      setBusy(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="offline-failures-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600" /> Offline sync — failed items
          </DialogTitle>
          <DialogDescription>
            These couldn't be saved to the server after reconnecting. Retry now, or discard if it will never go through (e.g. a typo that keeps failing validation).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 max-h-96 overflow-y-auto">
          {loading && <Loader2 className="h-5 w-5 animate-spin mx-auto text-muted-foreground" />}
          {!loading && total === 0 && (
            <p className="text-sm text-muted-foreground text-center py-2" data-testid="offline-failures-empty">Nothing failed — all clear.</p>
          )}
          {!loading && TYPES.map(({ key, icon: Icon, title }) => rows[key].length > 0 && (
            <div key={key}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                  <Icon className="h-3.5 w-3.5" /> {title} <Badge variant="outline" className="text-[10px]">{rows[key].length}</Badge>
                </div>
                <Button
                  size="sm" variant="outline" className="h-7 text-xs gap-1"
                  disabled={busy === `retry:${key}`}
                  onClick={() => retryType(key)}
                  data-testid={`offline-failures-retry-all-${key}`}
                >
                  {busy === `retry:${key}` ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />} Retry all
                </Button>
              </div>
              <div className="space-y-2">
                {rows[key].map((row) => {
                  const def = TYPES.find((t) => t.key === key);
                  const rowBusy = busy === `${key}:${row.local_id}`;
                  return (
                    <div key={row.local_id} className="border rounded-lg p-2.5 text-sm" data-testid={`offline-failure-row-${key}-${row.local_id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-medium truncate">{def.label(row)}</p>
                          <p className="text-xs text-red-600 mt-0.5">{row.last_error || 'Sync failed'}</p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">
                            {row.sync_attempts || 1} attempt{(row.sync_attempts || 1) === 1 ? '' : 's'} · queued {new Date(row.created_at).toLocaleString()}
                          </p>
                        </div>
                        <Button
                          size="icon" variant="ghost" className="h-7 w-7 text-red-600 hover:text-red-700 shrink-0"
                          disabled={rowBusy}
                          onClick={() => discardRow(key, row)}
                          data-testid={`offline-failure-discard-${key}-${row.local_id}`}
                          title="Discard"
                        >
                          {rowBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
